import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { graphql } from 'gatsby';
import { useTranslate } from 'gatsby-theme-soluble-source';
import { TextFieldSimple } from '@soluto-design/react';
import cls from '@soluto-design/styles/cls';

import { formatPost } from '../../utils/post';

import BlockGlossaryBar from 'design-system/src/components/organisms/BlockGlossaryBar';
import CardMediaLargeSection from 'design-system/src/components/organisms/CardMediaLargeSection';
import GridSection from 'design-system/src/components/organisms/GridSection';

import useSiteSearch from '../../hooks/useSiteSearch';

import type { Post, PostTopic } from '../../models/post';

interface BlogSectionProps {
    topics?: PostTopic[];
    topic?: PostTopic;
    allPosts: Post[];
    posts: Post[];
    pageNr?: number;
    numPages?: number;
    pathChild?: string;
    pathParent?: string;
    locale?: string;
    disableSearch?: boolean;
    title?: string;
}

export default function BlogSection({
    topics,
    topic,
    pageNr = 1,
    pathChild,
    pathParent,
    locale,
    disableSearch,
    posts,
    allPosts,
    title,
}: BlogSectionProps) {
    const [activeTopic, setActiveTopic] = useState<PostTopic | undefined>(
        topic,
    );
    const [currentPage, setCurrentPage] = useState<number>(
        pageNr < 1 ? 1 : pageNr,
    );
    const [showCleanButton, setShowCleanButton] = useState(false);
    const [inputValue, setInputValue] = useState('');

    const _ = useTranslate();

    const { response, setQuery, isLoading, query } = useSiteSearch(
        undefined,
        locale as string,
        process.env.GATSBY_BUILD_TRIGGER_ID ?? '',
    );

    const isSearch = useMemo(() => !!isLoading || !!query, [isLoading, query]);
    const isHomePage = useMemo(() => pageNr === 1, [pageNr]);
    const isNotHomePage = useMemo(() => !pageNr, [pageNr]);

    const strategyPagination = useMemo(() => {
        if (!isNotHomePage && !activeTopic?.href && !isSearch) {
            return 'routes-pagination';
        }

        return 'load-more';
    }, [activeTopic, isSearch, isNotHomePage]);

    const handleSearchSubmit = useCallback(
        (ev: React.SyntheticEvent) => {
            ev?.preventDefault();
            let value = inputValue.trim();

            if (value) {
                setQuery(value);
                setCurrentPage(1);
            }
        },
        [setQuery, inputValue],
    );

    const resetSearch = useCallback(() => {
        setQuery(undefined);
        setInputValue('');
        setCurrentPage(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const resetSearchIfContents = useCallback(() => {
        let value = inputValue.trim();
        if (!value && query) {
            resetSearch();
        }
    }, [inputValue, query, resetSearch]);

    const handleClickTopic = useCallback((value) => {
        setActiveTopic(value);
        setCurrentPage(1);
    }, []);

    const thisResults = useMemo(() => {
        if (!query) return posts;

        const { data } = response ?? {};

        const normalizePath = (path: string) => {
            return `/${path
                .split('/')
                .filter(Boolean)
                .filter((str) => str !== locale)
                .join('/')}`;
        };

        if (data) {
            const postsForSearch = activeTopic?.href
                ? allPosts?.filter(
                      (post) =>
                          post.topic?.href &&
                          post.topic.href === activeTopic.href,
                  )
                : allPosts;

            const results = data.reduce((acc, current) => {
                const item = postsForSearch.find((item: Post) => {
                    const url = new URL(current?.attributes.url);

                    // Equalize the pathname to remove the locale and slashes
                    let pathname = normalizePath(url.pathname);

                    let itemSlug = normalizePath(item.href);

                    return pathname === itemSlug;
                });

                if (
                    !item ||
                    acc.some((prevItem) => prevItem.href === item.href)
                )
                    return acc;

                acc.push(item);

                return acc;
            }, [] as any[]);

            return results;
        }

        return [];
    }, [query, posts, response, locale, activeTopic, allPosts]);

    const [largePost, defaultPosts] = useMemo(() => {
        if (!posts?.length) return [undefined, []];

        if (isSearch) {
            return [
                undefined,
                thisResults?.map((post) =>
                    formatPost(post, _['blog.list.read-more'].text),
                ),
            ];
        }

        if (activeTopic?.href && allPosts?.length) {
            const filteredPosts = allPosts.filter(
                (post) =>
                    post.topic?.href && post.topic.href === activeTopic.href,
            );

            return [
                undefined,
                filteredPosts.map((post) =>
                    formatPost(post, _['blog.list.read-more'].text),
                ),
            ];
        }

        if (isHomePage) {
            const [first, ...rest] = posts;

            return [
                formatPost(first, _['blog.list.read-more'].text, true),
                rest.map((post) =>
                    formatPost(post, _['blog.list.read-more'].text),
                ),
            ];
        }

        return [
            undefined,
            posts.map((post) =>
                formatPost(post, _['blog.list.read-more'].text),
            ),
        ];
    }, [posts, isSearch, activeTopic, allPosts, isHomePage, thisResults, _]);

    const finalTopics = useMemo(() => {
        if (!topics?.length) return [];

        const theseTopics = activeTopic
            ? topics?.map((topic) => ({
                  ...topic,
                  active: topic.href === activeTopic.href,
                  disabled: topic.href === activeTopic.href,
              }))
            : topics || [];

        return [
            {
                label: _['blog.list.category.all'].text,
                active: !activeTopic?.href,
                disabled: !activeTopic?.href,
                ...(!!pathParent && { href: pathParent }),
            },
            ...(theseTopics as PostTopic[]),
        ];
    }, [topics, activeTopic, _, pathParent]);

    useEffect(() => {
        setShowCleanButton(!!inputValue);
    }, [inputValue]);

    return (
        <div className="mt-xl">
            {!!allPosts?.length && !disableSearch && (
                <div className="container">
                    <form onSubmit={handleSearchSubmit}>
                        <TextFieldSimple
                            leftIcon="search"
                            leftIconButtonProps={{
                                type: 'submit',
                            }}
                            rightIcon={showCleanButton ? 'close' : undefined}
                            rightIconButtonProps={{
                                onClick: resetSearch,
                            }}
                            type="text"
                            step="any"
                            placeholder={_['blog.search.placeholder']?.text}
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                            classes={{
                                input: '!text-b300',
                            }}
                            onBlur={resetSearchIfContents}
                        />
                    </form>
                </div>
            )}
            {!!finalTopics?.length && (
                <div
                    className={cls(
                        'xl:container xl:pr-0 xl:[&>div]:pl-0',
                        !largePost && 'pb-[48px]',
                        !disableSearch && 'pt-[24px]',
                    )}>
                    <BlockGlossaryBar
                        tags={finalTopics}
                        {...(!topic && { onClick: handleClickTopic })}
                    />
                </div>
            )}
            {!!largePost && <CardMediaLargeSection {...largePost} />}
            {(!!defaultPosts?.length || isSearch) && (
                <GridSection
                    className={cls(
                        (disableSearch && !finalTopics.length && !largePost) ||
                            isSearch ||
                            isLoading
                            ? '!mt-0'
                            : '!-mt-xl',
                    )}
                    variant="grid-media-1"
                    title={
                        isSearch
                            ? isLoading
                                ? `<i>${_['blog.search.loading'].text}</i>`
                                : _['blog.list.results.title'].text
                                      ?.replace('{query}', query)
                                      .replace(
                                          '{nr}',
                                          thisResults?.length || 'No',
                                      )
                            : title
                    }
                    items={defaultPosts as any}
                    viewMore={
                        !isLoading && strategyPagination === 'routes-pagination'
                            ? {
                                  label: _['blog.list.view-more'].text,
                                  href: (
                                      pathChild || '/blog/page/{page}/'
                                  ).replace('{page}', `${pageNr + 1}`),
                              }
                            : {
                                  label: _['blog.list.view-more'].text,
                                  onClick: () => {
                                      setCurrentPage(
                                          (prevCurrentPage) =>
                                              prevCurrentPage + 1,
                                      );
                                  },
                              }
                    }
                    pagination={
                        strategyPagination === 'load-more'
                            ? {
                                  initialItems: 12,
                                  itemsPerPage: 12,
                                  page: currentPage,
                              }
                            : undefined
                    }
                />
            )}
        </div>
    );
}

export const query = graphql`
    fragment DatoCmsBlogSectionParentPage on DatoCmsPage {
        ...BreadcrumbPart
        slug
        sections {
            typename: __typename
            ... on DatoCmsHeroComboSection {
                title
                titleTag
                description
            }
            ... on DatoCmsBlogSection {
                locale
                numPages
                pathChild
            }
            ... on DatoCmsBannerNewsletterSection {
                ...DatoCmsBannerNewsletterSection
            }
        }
    }

    fragment DatoCmsBlogSection on DatoCmsBlogSection {
        allPosts {
            ...DatoCmsBlogPostItem
        }
        posts {
            ...DatoCmsBlogPostItem
            largeMedia: image {
                ...SourceMedia
                gatsbyImageData(
                    placeholder: BLURRED
                    width: 1508
                    height: 1508
                    imgixParams: { fit: "crop", w: "1508", h: "1508" }
                )
            }
        }
        topics {
            ...DatoCmsTopic
        }
        locale
        numPages
        pathChild
    }
`;
